// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckmarkCircle2Fill from "@store/Library/Components/Toolkit/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import React, { FC, ReactNode } from "react";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import getColor from "@insite/mobius/utilities/getColor";
import siteMessage from "@insite/client-framework/SiteMessage";
import { useHomePageUrl } from "@toolkit/Framework/Store/Links/LinksHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";

export interface ConfirmationMessagePresentationProps extends ExpandStyles<ConfirmationMessageStyles> {}

export interface ConfirmationMessageProps extends ConfirmationMessagePresentationProps {
    headline?: ReactNode;
    body?: ReactNode;
    buttonText?: ReactNode;
    buttonHref?: string;
}

export interface ConfirmationMessageStyles {
    wrapper?: InjectableCss;
    headlineIcon?: IconPresentationProps;
    headline?: TypographyPresentationProps;
    body?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
}

export const confirmationMessageStyles: ConfirmationMessageStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: ${pxToRem(50, 0)};
        `,
    },
    headline: {
        forwardAs: "h2",
        css: css`
            ${spacingStyles(null, pxToRem(22))}
            color: ${getColor("primary")};
            font-size: ${pxToRem(24)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
        `,
    },
    headlineIcon: {
        src: CheckmarkCircle2Fill,
        size: 24,
        css: css`
            position: relative;
            top: ${pxToRem(4)};
            color: ${getColor("primary")};
            margin-right: ${pxToRem(8)};
        `,
    },
    body: {
        forwardAs: "p",
        css: css`
            ${spacingStyles(null, pxToRem(50))}
        `,
    },
    button: {
        variant: "primary",
        sizeVariant: "large",
    },
};

const ConfirmationMessage: FC<ConfirmationMessageProps> = ({
    body,
    buttonHref,
    buttonText,
    extendedStyles,
    headline,
    overridedStyles,
}) => {
    const styles = useExpandProps(confirmationMessageStyles, extendedStyles, overridedStyles);
    const homePageUrl = useHomePageUrl();

    const hasHeadline = headline !== "";
    const hasBody = body !== "";
    const hasButton = buttonText !== "";

    if (!hasHeadline && !hasBody && !hasButton) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {hasHeadline && (
                <Typography {...styles.headline}>
                    {styles.headlineIcon?.src ? <Icon {...styles.headlineIcon} /> : null}
                    {headline || translate("Thank you for your request!")}
                </Typography>
            )}
            {hasBody && (
                <Typography {...styles.body}>{body || siteMessage("ContactUs_ConfirmationMessage_Body")}</Typography>
            )}
            {hasButton && (
                <Button {...styles.button} href={buttonHref || homePageUrl}>
                    {buttonText || translate("Go back to shopping")}
                </Button>
            )}
        </StyledWrapper>
    );
};

export default ConfirmationMessage;
